import { AfterViewInit, Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { FwcModal } from '../fwc-lib.js';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { AppConfigService } from './main/app-config.service';
import { AppConstants } from './common/constants/app-constants';
import { AuthService } from './common/services/auth.service';
import { DataService } from './common/services/data.service';
import { HomeService } from './common/services/home.service';
import { TokenService } from './common/services/token.service';
import { UserCacheService } from './common/services/user-cache.service';
import { UtilityService } from './common/services/utility.service';
import { datadogRum } from '@datadog/browser-rum';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  title = 'sae';
  browserIE = false;
  idleState = 'Not started.';
  timedOut = false;
  lastPing?: Date = null;
  modalMsg = '';
  userLoggedInstanceSubscription: any;
  public environment: any;
  constructor(
    public homeService: HomeService,
    private route: ActivatedRoute,
    private router: Router,
    public userCacheService: UserCacheService,
    public dataService: DataService,
    public tokenService: TokenService,
    public utilityService: UtilityService,
    public logoutService: TokenService,
    public authServ: AuthService, private idle: Idle,
    public appConfigService: AppConfigService) {
      this.environment = this.appConfigService.getConfig();
    if (this.environment.environment === 'prod') {
      datadogRum.init({
        applicationId: '0c117935-2d5e-4d93-9e29-5312105006c0',
        clientToken: 'pub3ff490e4f27a99889640a8dcaf7ef06a',
        site: 'datadoghq.com',
        service: 'student-advantage-enroll---prod',
        env: this.environment.environment,
        // Specify a version number to identify the deployed version of your application in Datadog 
        // version: '1.0.0',
        sessionSampleRate: 100,
        sessionReplaySampleRate: 20,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
    });
    } else {
      datadogRum.init({
        applicationId: '1d5a2ad5-ae9f-4e36-9918-9fb243a935c8',
        clientToken: 'puba76cb7c84d6ebf5c5604aae406fe827f',
        site: 'datadoghq.com',
        service: 'student-advantage-enroll',
        env: this.environment.environment,
        // Specify a version number to identify the deployed version of your application in Datadog 
        // version: '1.0.0',
        sessionSampleRate: 100,
        sessionReplaySampleRate: 20,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
      });
    }
    // sets an idle timeout of 5 seconds, for testing purposes.
    idle.setIdle(this.environment.idleTimeLength);
    // sets a timeout period of 5 seconds. after 10 seconds of inactivity, the user will be considered timed out.
    idle.setTimeout(this.environment.sessionTimeoutLength);
    // sets the default interrupts, in this case, things like clicks, scrolls, touches to the document
    idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    idle.onIdleEnd.subscribe(() => {
      this.idleState = 'No longer idle.'
      this.reset();
    });

    idle.onTimeout.subscribe(() => {
      this.closeIdleModal();
      this.idleState = 'Timed out!';
      this.timedOut = true;
      this.logout();
    });

    idle.onIdleStart.subscribe(() => {
      this.idleState = 'You\'ve gone idle!'
      this.openIdleModal();
    });

    idle.onTimeoutWarning.subscribe((countdown) => {
      this.idleState = 'You will time out in ' + countdown + ' seconds!'
      this.modalMsg = 'Your session will expire in ' + countdown + ' seconds.'
    });

    // Reload event binding to allow caching of data despite page refresh.
    window.onbeforeunload = () => {
      userCacheService.backupToLocalStorage();
    };

    this.userLoggedInstanceSubscription = this.dataService.userLoggedInstance.subscribe(userLoggedIn => {
      if (userLoggedIn) {
        idle.watch()
        this.timedOut = false;
      } else {
        idle.stop();
      }
    });

    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        if (!event.url || /^\/school\/[a-zA-Z0-9]+\/?/.test(event.url)) {
          this.manageStory(event.url);
        }else{
          this.manageParticipation(event.url);
        }
      }
    });
    const fwcModal = new FwcModal();
    fwcModal.build();
    this.browserIE = this.isIE();
  }
  ngOnDestroy(): void {
    if (this.userLoggedInstanceSubscription) {
      this.userLoggedInstanceSubscription.unsubscribe();
    }
  }
  ngOnInit() {
    const urlParams = new URLSearchParams(window.location.search);
    const userId = urlParams.get('user_id');
    const token = urlParams.get('token');
    if (userId && /^\d+$/.test(userId) && token && /\/\d+\/secure\/home$/.test(window.location.pathname)) {
      const participationId = window.location.pathname.split('/')[1];
      this.ssoCrmAdmin(userId, token, participationId);
    }
  }
  ngAfterViewInit(): void {
    if (this.browserIE) {
      this.openModal();
    }
  }

  ssoCrmAdmin(userId: string, jwtToken: string, participationId: string) {
    this.userCacheService.setUserData(AppConstants.CLIENT_CORRELATION_ID, 'admin-' + Date.now());
    this.userCacheService.setUserData(AppConstants.USERID, 'ASAUSER' + userId);
    this.userCacheService.setUserData(AppConstants.TOKEN, jwtToken);
    this.userCacheService.setUserData(AppConstants.USER_LOGGED_IN, true);
    this.dataService.doAnnouncement({ topic: AppConstants.TOKEN, message: jwtToken });
    this.dataService.userLoggedIn(true);
    if (participationId) {
      this.navigateToDashboard(parseInt(participationId, 10), `/${participationId}/secure/home`);
    }
  }

  reset() {
    this.idle.watch();
    this.timedOut = false;
  }

  hideChildModal(): void {
    this.closeIdleModal();
  }

  stay() {
    this.closeIdleModal();
    this.reset();
  }

  logout() {
    this.closeIdleModal();
    const participationId = this.userCacheService.getUserData(AppConstants.PARTICIPATION_ID);
    const micrositeId = this.userCacheService.getUserData(AppConstants.MICROSITE_ID);
    this.logoutService.logout().subscribe(data => {
      this.userCacheService.eraseAllUserData();
      this.dataService.userLoggedIn(false);
      window.location.href = '/logout'
    }, err => {
      this.userCacheService.eraseAllUserData();
      this.dataService.userLoggedIn(false);
      window.location.href = '/logout'
    })
  }

  

  navigateToDashboard(participationId: number, url: string) {
    this.userCacheService.eraseUserData(AppConstants.ENROLLMENT_ID);
    this.userCacheService.eraseUserData(AppConstants.TERMS);
    this.userCacheService.eraseUserData(AppConstants.WaiverModel);
    this.userCacheService.eraseUserData(AppConstants.NewEnrollmentModel);
    this.userCacheService.eraseUserData(AppConstants.UserDetails);
    this.homeService.getLandingData(participationId).subscribe((resp: any) => {
      const token = this.userCacheService.getUserData(AppConstants.TOKEN);
      if (resp.status === AppConstants.SUCCESS_STATUS) {
        this.updateCache(participationId, resp);
        this.dataService.doAnnouncement({ topic: AppConstants.TOKEN, message: token });
        if ((url === `/${participationId}/secure/waiver`)) {
          this.router.navigate([url]);
        } else {
          this.router.navigate([`/${participationId}/secure/home`]);
        }
      } else {
        this.router.navigate(['/']);
      }
    }, err => {
      this.router.navigate(['/']);
    });
  }

  manageParticipation(url: string): void {
    this.userCacheService.eraseUserData(AppConstants.STORY_ID);
    if (!url || /^\/\d+\/?$/.test(url)) {
      return;
    }
    const participationId = this.userCacheService.getUserData(AppConstants.PARTICIPATION_ID);
    let tmpParticipationId = null;
    const urlObj = url.match(/^\/(\d+)\/?/);
    if (urlObj && urlObj.length > 0) {
      tmpParticipationId = parseInt(urlObj[1], 10);
    }
    if (tmpParticipationId && (!participationId || (participationId !== tmpParticipationId))) {
      this.userCacheService.eraseUserData(AppConstants.ENROLLMENT_ID);
      if (this.authServ.isAuthenticated() && (urlObj.input !== `/${tmpParticipationId}/home`)) {
        this.navigateToDashboard(tmpParticipationId, url);
      } else if (urlObj.input !== `/${tmpParticipationId}/home`) {
        this.getLandingInfo(tmpParticipationId);
      } else {
        this.navigateToDashboard(participationId, `/${participationId}/home`);
      }
    } else if (!tmpParticipationId) {
      this.clearCache();
    }
  }

  manageStory(url: string): void {
    this.userCacheService.setUserData(AppConstants.PARTICIPATION_ID, null);
    if (!url || /^\/school\/[a-zA-Z0-9]+\/?$/.test(url)) {
      return;
    }
    const storyId = this.userCacheService.getUserData(AppConstants.STORY_ID);
    let tmpStoryId = null;
    const urlObj = url.match(/^\/school\/([a-zA-Z0-9]+)\/?/);
    if (urlObj) {
      tmpStoryId = urlObj[1];
    }
    if (tmpStoryId && (!storyId || (storyId !== tmpStoryId))) {
      this.userCacheService.eraseUserData(AppConstants.ENROLLMENT_ID);
      if (this.authServ.isAuthenticated() && (urlObj.input !== `/school/${tmpStoryId}/secure/dashboard`)) {
        this.navigateToStoryDashboard(tmpStoryId, url);
      } else if (urlObj.input !== `/school/${tmpStoryId}/secure/dashboard`) {
        this.getStoryLandingInfo(tmpStoryId);
      }
    } else if (!tmpStoryId) {
      this.clearCache();
    } else if (this.authServ.isAuthenticated() &&/^\/school\/([a-zA-Z0-9]+)\/login-okta/.test(url) && tmpStoryId && storyId && (storyId === tmpStoryId)) {
      this.navigateToStoryDashboard(tmpStoryId, url);
    }
  }

  navigateToStoryDashboard(storyId: string, url: string) {
    this.userCacheService.eraseUserData(AppConstants.UserDetails);
    this.homeService.getStoryLandingInfo(storyId).subscribe((resp: any) => {
      const token = this.userCacheService.getUserData(AppConstants.TOKEN);
      if (resp.status === AppConstants.SUCCESS_STATUS) {
        this.storyUpdateCache(storyId, resp);
        this.dataService.doAnnouncement({ topic: AppConstants.TOKEN, message: token });
          this.router.navigate([`/school/${storyId}/secure/dashboard`]);
      } else {
        this.router.navigate(['/']);
      }
    }, err => {
      this.router.navigate(['/']);
    });
  }

  getStoryLandingInfo(storyId: any) {
    this.clearCache();
    if (!storyId) {
      return;
    }
    this.homeService.getStoryLandingInfo(storyId).subscribe((resp: any) => {
      if (resp.status === 'SUCCESS') {
        // this.contentUpdate();
        this.storyUpdateCache(storyId, resp);
      } else if (resp.status === AppConstants.ERROR_STATUS) {
        this.router.navigate(['/']);
      }
    }, err => {
      this.router.navigate(['/']);
    });
  }

  storyUpdateCache(storyId: any, response: any): void {
    const footer = {
      privacy: response.privacy,
      legal: response.legal,
      contactInfo: response.contactInfo,
      tagline: response.tagline
    };
    const header = {
      brandLogo: response.brandLogo,
      universityLogo: response.universityLogo,
      universityLabel: response.universityLabel
    };
    this.userCacheService.setUserData(AppConstants.ENROLLMENT_TYPE, response.enrollmentType);
    this.userCacheService.setUserData(AppConstants.TERMS, response.terms);
    this.userCacheService.setUserData(AppConstants.STORY_ID, storyId);
    this.userCacheService.setUserData(AppConstants.GLOBAL_HEADER, header);
    this.userCacheService.setUserData(AppConstants.GLOBAL_FOOTER, footer);
    this.userCacheService.setUserData(AppConstants.UNIVERSITY_NAME, response.universityName);
    this.userCacheService.setUserData(AppConstants.MICROSITE_ID, response.microSiteId);
    this.userCacheService.setUserData(AppConstants.MICORSITE_TITLE, response.microSiteTitle);
    this.userCacheService.setUserData(AppConstants.MICROSITE_SUBTITLE, response.microSiteSubTitle);
    this.userCacheService.setUserData(AppConstants.MICROSITE_STORY_ID, response.micrositeStoryId);
    
    // Save to localStorage
    localStorage.setItem('storyId', storyId);
    localStorage.removeItem('participantId');
    if (response.microSiteId) {
      localStorage.setItem('micrositeId', response.microSiteId);
    }
    this.dataService.doAnnouncement({ topic: AppConstants.STORY_ID, message: storyId });
    this.dataService.doAnnouncement({ topic: AppConstants.GLOBAL_HEADER, message: header });
    this.dataService.doAnnouncement({ topic: AppConstants.GLOBAL_FOOTER, message: footer });
    this.dataService.doAnnouncement({ topic: AppConstants.MICORSITE_TITLE, message: response.microSiteTitle });
    this.dataService.doAnnouncement({ topic: AppConstants.MICROSITE_STORY_ID, message: response.micrositeStoryId });
  }

  clearCache(): void {
    this.userCacheService.eraseAllUserData();
    this.dataService.doAnnouncement({ topic: AppConstants.STORY_ID, message: null });
    this.dataService.doAnnouncement({ topic: AppConstants.PARTICIPATION_ID, message: null });
    this.dataService.doAnnouncement({ topic: AppConstants.GLOBAL_HEADER, message: null });
    this.dataService.doAnnouncement({ topic: AppConstants.GLOBAL_FOOTER, message: null });

    this.userCacheService.setUserData(AppConstants.STORY_ID, null);
    this.userCacheService.setUserData(AppConstants.PARTICIPATION_ID, null);
    this.userCacheService.setUserData(AppConstants.GLOBAL_HEADER, null);
    this.userCacheService.setUserData(AppConstants.GLOBAL_FOOTER, null);
    this.userCacheService.setUserData(AppConstants.UNIVERSITY_NAME, null);
    this.userCacheService.setUserData(AppConstants.PARTICIPATION_LABEL, null);
    this.userCacheService.setUserData(AppConstants.CLIENT_CORRELATION_ID, null);
    this.userCacheService.setUserData(AppConstants.USERID, null);
    this.userCacheService.setUserData(AppConstants.USER_LOGGED_IN, false);
    this.userCacheService.setUserData(AppConstants.TOKEN, null);
  }

  getLandingInfo(participationId: number): void {
    this.clearCache();
    if (isNaN(participationId)) {
      return;
    }
    this.homeService.getLandingData(participationId).subscribe((resp: any) => {
      if (resp.status === AppConstants.SUCCESS_STATUS) {
        this.updateCache(participationId, resp);
      } else if (resp.status === AppConstants.ERROR_STATUS) {
        this.router.navigate(['/']);
      }
    }, err => {
      this.router.navigate(['/']);
    });
  }

  updateCache(participationId: number, response: any): void {
    const footer = {
      privacy: response.privacy,
      legal: response.legal,
      contactInfo: response.contactInfo,
      tagline: response.tagline
    };
    const header = {
      brandLogo: response.brandLogo,
      universityLogo: response.universityLogo,
      universityLabel: response.universityLabel
    };
    this.userCacheService.setUserData(AppConstants.ENROLLMENT_TYPE, response.enrollmentType);
    this.userCacheService.setUserData(AppConstants.TERMS, response.terms);
    this.userCacheService.setUserData(AppConstants.PARTICIPATION_ID, participationId);
    this.userCacheService.setUserData(AppConstants.GLOBAL_HEADER, header);
    this.userCacheService.setUserData(AppConstants.IS_PAYMENT_REQUIRED, response.payment);
    this.userCacheService.setUserData(AppConstants.GLOBAL_FOOTER, footer);
    this.userCacheService.setUserData(AppConstants.UNIVERSITY_NAME, response.universityName);
    this.userCacheService.setUserData(AppConstants.PARTICIPATION_LABEL, response.participationLabel);
    this.userCacheService.setUserData(AppConstants.MICROSITE_ID, response.microSiteId);
    this.userCacheService.setUserData(AppConstants.MICORSITE_TITLE, response.microSiteTitle);
    this.userCacheService.setUserData(AppConstants.MICROSITE_SUBTITLE, response.microSiteSubTitle);
    this.userCacheService.setUserData(AppConstants.MICROSITE_STORY_ID, response.storyId);
    
    // Save to localStorage
    localStorage.setItem('participantId', participationId.toString());
    localStorage.removeItem('storyId');
    if (response.microSiteId) {
      localStorage.setItem('micrositeId', response.microSiteId);
    }
    this.dataService.doAnnouncement({ topic: AppConstants.PARTICIPATION_ID, message: participationId });
    this.dataService.doAnnouncement({ topic: AppConstants.GLOBAL_HEADER, message: header });
    this.dataService.doAnnouncement({ topic: AppConstants.GLOBAL_FOOTER, message: footer });
    this.dataService.doAnnouncement({ topic: AppConstants.UNIVERSITY_NAME, message: response.universityName });
    this.dataService.doAnnouncement({ topic: AppConstants.PARTICIPATION_LABEL, message: response.participationLabel });
    this.dataService.doAnnouncement({ topic: AppConstants.IS_PAYMENT_REQUIRED, message: response.payment });
    this.dataService.doAnnouncement({ topic: AppConstants.TERMS, message: response.terms });
    this.dataService.doAnnouncement({ topic: AppConstants.MICROSITE_ID, message: response.microSiteId });
    this.dataService.doAnnouncement({ topic: AppConstants.MICORSITE_TITLE, message: response.microSiteTitle });
    this.dataService.doAnnouncement({ topic: AppConstants.MICROSITE_STORY_ID, message: response.storyId });
  }

  openModal(): void {
    const modal = document.getElementById('ie_alert');
    modal.setAttribute('open', '');
  }

  closeModal(): void {
    const modal = document.getElementById('ie_alert');
    modal.removeAttribute('open');
    this.logout();
  }

  openIdleModal(): void {
    const modal = document.getElementById('timeOutModal');
    modal.setAttribute('open', '');
  }

  closeIdleModal(): void {
    const modal = document.getElementById('timeOutModal');
    modal.removeAttribute('open');
  }

  isIE(): boolean {
    const agent = window.navigator.userAgent.toLowerCase();
    return agent.indexOf('trident') > -1;
  }
}
